import React, { useEffect, useState, useRef, useMemo } from "react"; // Import useMemo
import {
    Grid,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    MenuItem,
    TextField,
    Chip,
    Paper,
    FormControl,
    Tooltip,
    CircularProgress,
    Stack,
    InputLabel,
    Select,
    Typography,
    Divider,
    FormHelperText,
    keyframes
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    Phone as PhoneIcon,
    ReportProblem as ReportProblemIcon,
    Description as DescriptionIcon,
    Image as ImageIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Clear as ClearIcon
} from "@mui/icons-material";
import { format, isValid, startOfDay, endOfDay } from "date-fns";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { ServerBaseUrlForStaticImages } from "constants";

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const StatusBadge = ({ status }) => {
    let color, icon;
    switch (status) {
        case "Resolved":
            color = "success";
            icon = <CheckCircleIcon />;
            break;
        case "In Progress":
            color = "warning";
            icon = <HourglassEmptyIcon />;
            break;
        case "Pending":
        default:
            color = "error";
            icon = <ReportProblemIcon />;
            break;
    }
    return <Chip icon={icon} label={status} color={color} size="small" sx={{ fontWeight: 'medium' }} />;
};

const InfoCard = ({ icon, title, children, index }) => (
    <Paper
        elevation={2}
        sx={{
            p: 2, borderRadius: 3, height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            animation: `${slideUp} 0.5s ease-in-out forwards`,
            animationDelay: `${index * 100}ms`,
            opacity: 0,
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <Box sx={{
                bgcolor: 'info.main',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                p: 1,
            }}>

                {icon}
            </Box>
            <MDTypography variant="h6" color="white">{title}</MDTypography>
        </Stack>
        {children}
    </Paper>
);

const columns = (handleView) => [
    { Header: "S.No.", accessor: "s_no", width: "5%" },
    { Header: "Phone", accessor: "customer_phone_number" },
    {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => (value && isValid(new Date(value)) ? format(new Date(value), 'dd MMM, hh:mm a') : 'N/A')
    },
    { Header: "Complaint Type", accessor: "complaint_type" },
    {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => <StatusBadge status={value} />
    },
    {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        Cell: ({ row }) => (
            <Tooltip title="View Details">
                <IconButton color="info" onClick={() => handleView(row.original)}>
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>
        ),
    },
];

function FeedbackViewPage() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [openZoom, setOpenZoom] = useState(false);
    const [zoomImageUrl, setZoomImageUrl] = useState('');
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const navigate = useNavigate();

    // State for global search
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const initialFeedbackState = useRef(null);

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    // Memoized filtered and searched feedbacks
    const filteredAndSearchedFeedbacks = useMemo(() => {
        let currentFilteredData = [...feedbacks];

        // Apply date filters
        if (fromDate) {
            const startDate = startOfDay(new Date(fromDate));
            currentFilteredData = currentFilteredData.filter(fb => isValid(new Date(fb.date)) && new Date(fb.date) >= startDate);
        }
        if (toDate) {
            const endDate = endOfDay(new Date(toDate));
            currentFilteredData = currentFilteredData.filter(fb => isValid(new Date(fb.date)) && new Date(fb.date) <= endDate);
        }

        // Apply global search
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            currentFilteredData = currentFilteredData.filter(feedback =>
                feedback.customer_phone_number.toLowerCase().includes(lowerCaseQuery) ||
                feedback.complaint_type.toLowerCase().includes(lowerCaseQuery) ||
                feedback.status.toLowerCase().includes(lowerCaseQuery) ||
                (feedback.description && feedback.description.toLowerCase().includes(lowerCaseQuery))
            );
        }

        return currentFilteredData;
    }, [feedbacks, fromDate, toDate, searchQuery]);


    // Adjust page if current page is out of bounds after filtering and searching
    useEffect(() => {
        const totalItems = filteredAndSearchedFeedbacks.length;
        if (page * rowsPerPage >= totalItems && totalItems > 0) {
            setPage(0); // Go to first page if current page is now empty
        } else if (totalItems === 0 && page !== 0) {
            setPage(0); // If no items, ensure page is 0
        }
    }, [page, rowsPerPage, filteredAndSearchedFeedbacks]);


    const fetchFeedbacks = async () => {
        setLoading(true);
        try {
            const res = await api.get("/feedback/");
            if (Array.isArray(res.data)) {
                const sortedData = res.data.sort((a, b) => {
                    if (a.status === 'Pending' && b.status !== 'Pending') return -1;
                    if (a.status !== 'Pending' && b.status === 'Pending') return 1;
                    return new Date(b.date) - new Date(a.date);
                });
                setFeedbacks(sortedData);
            } else {
                console.warn("API returned non-array data:", res.data);
                setFeedbacks([]);
            }
        } catch (error) {
            console.error("Error fetching feedbacks:", error);
            Swal.fire("Error", "Could not fetch feedback data.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setSearchQuery(""); // Clear search query as well
    };

    const handleView = (data) => {
        setSelectedFeedback(data);
        setRemarks(data.remarks || "");
        initialFeedbackState.current = {
            status: data.status,
            remarks: data.remarks || "",
            description: data.description || ""
        };
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedFeedback(null);
        setRemarks("");
        initialFeedbackState.current = null;
    };

    const handleImageClick = (imageUrl) => {
        setZoomImageUrl(imageUrl);
        setOpenZoom(true);
    };

    const handleStatusChange = async () => {
        if (!selectedFeedback) {
            Swal.fire("Error", "No feedback selected.", "error");
            return;
        }

        const { feedback_id } = selectedFeedback;
        const currentStatus = selectedFeedback.status;
        const currentRemarks = remarks;
        const currentDescription = selectedFeedback.description;

        const isStatusChanged = currentStatus !== initialFeedbackState.current.status;
        const isRemarksChanged = currentRemarks !== initialFeedbackState.current.remarks;
        const isDescriptionChanged = currentDescription !== initialFeedbackState.current.description;

        if (!isStatusChanged && !isRemarksChanged && !isDescriptionChanged) {
            Swal.fire("Info", "No changes to save.", "info");
            return;
        }

        setIsUpdating(true);
        try {
            const res = await api.put(`/feedback/${feedback_id}`, {
                status: currentStatus,
                remarks: currentRemarks,
                description: currentDescription
            });
            if (res.data.message) {
                Swal.fire("Success", res.data.message, "success");
                setFeedbacks(prev => prev.map(fb =>
                    fb.feedback_id === feedback_id
                        ? { ...fb, status: currentStatus, remarks: currentRemarks, description: currentDescription }
                        : fb
                ));
                handleCloseView();
            } else {
                Swal.fire("Error", res.data.error || "Failed to update data", "error");
            }
        } catch (error) {
            console.error("Update failed", error);
            Swal.fire("Error", "Failed to update feedback.", "error");
        } finally {
            setIsUpdating(false);
        }
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    // Calculate the subset of data for the current page from filteredAndSearchedFeedbacks
    const startIndex = page * rowsPerPage;
    const paginatedFeedbacks = filteredAndSearchedFeedbacks.slice(startIndex, startIndex + rowsPerPage);


    const tableRows = paginatedFeedbacks.map((fb, index) => ({
        ...fb,
        s_no: startIndex + index + 1 // Ensure S.No. is correct for the paginated view
    }));

    const disableRemarksField =
        selectedFeedback?.status === 'Resolved' &&
        initialFeedbackState.current?.status === 'Resolved' &&
        remarks === initialFeedbackState.current?.remarks;

    const isSaveButtonDisabled =
        isUpdating ||
        !selectedFeedback ||
        (selectedFeedback.status === initialFeedbackState.current?.status &&
            remarks === initialFeedbackState.current?.remarks &&
            selectedFeedback.description === initialFeedbackState.current?.description);


    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={2} px={2}
                                variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" gap={2}>
                                    <MDTypography variant="h6" color="white">
                                        User Complaints
                                        <Chip label={`Showing: ${filteredAndSearchedFeedbacks.length}`} color="secondary" size="small" sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                                    </MDTypography>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center"> {/* Adjusted stack for filters and search */}
                                        {/* Global Search Input */}
                                        <TextField
                                            label="Search Complaints"
                                            variant="outlined"
                                            size="small"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            sx={{
                                                width: { xs: '100%', sm: '200px' },
                                                "& .MuiInputBase-root": {
                                                    bgcolor: 'rgba(220, 211, 211, 0.15)', // Light translucent background for the input area
                                                    color: 'white', // This sets the color of the text typed into the input
                                                    '&:hover fieldset': {
                                                        borderColor: 'white', // Border color on hover
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'white', // Border color when the input is focused
                                                    },
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: 'rgba(247, 242, 242, 0.4)' // Default border color
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: 'white', // Color of the label itself
                                                    '&.Mui-focused': {
                                                        color: 'white', // Label color when the input is focused
                                                    },
                                                },
                                                // This is the key for the placeholder color!
                                                "& .MuiInputBase-input::placeholder": {
                                                    color: 'white', // Set the placeholder text color to white
                                                    opacity: 0.7,   // Add a slight opacity to make it look like a placeholder
                                                },
                                            }}
                                            InputProps={{
                                                // While already set in "& .MuiInputBase-root", keeping this ensures consistency.
                                                style: { color: 'white' }
                                            }}
                                        />
                                        <TextField type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{
                                            "& .MuiInputBase-root": { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: 'rgba(255,255,255,0.4)' }
                                        }} />
                                        <Typography color="white">to</Typography>
                                        <TextField type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{
                                            "& .MuiInputBase-root": { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: 'rgba(255,255,255,0.4)' }
                                        }} />
                                        <Tooltip title="Clear Filters">
                                            <IconButton onClick={handleClearFilters} size="small" sx={{ color: 'white' }}>
                                                <ClearIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </MDBox>
                            <MDBox p={3} sx={{ animation: `${fadeIn} 0.5s ease-in-out` }}>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                                ) : tableRows.length > 0 ? (
                                    <DataTable
                                        table={{ columns: columns(handleView), rows: tableRows }}
                                        isSorted={true}
                                        canSearch={false} // Disable DataTable's internal search as we're doing global search
                                        entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries={true}
                                        noEndBorder
                                        pagination={{
                                            variant: "contained",
                                            color: "dark",
                                            page: page,
                                            onPageChange: handlePageChange,
                                            rowsPerPage: rowsPerPage,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredAndSearchedFeedbacks.length, // Total count based on filtered and searched data
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ textAlign: 'center', p: 5 }}>
                                        <MDTypography variant="h6" color="text">No feedback records found.</MDTypography>
                                    </Box>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Dialog open={openView} onClose={handleCloseView} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" color="white">
                            Feedback Details
                        </MDTypography>
                        <IconButton onClick={handleCloseView} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: 'grey.200', p: { xs: 1, sm: 2, md: 3 } }}>
                    {selectedFeedback && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={7}>
                                <Stack spacing={3}>
                                    <InfoCard index={0} icon={<PhoneIcon />} title="Contact & Complaint">
                                        <Typography variant="body2" mb={1}><strong>Phone:</strong> {selectedFeedback.customer_phone_number}</Typography>
                                        <Typography variant="body2" mb={1}><strong>Type:</strong> {selectedFeedback.complaint_type}</Typography>
                                        <Typography variant="body2">
                                            <strong>Date:</strong> {selectedFeedback.date ? format(new Date(selectedFeedback.date), 'dd MMMM, hh:mm a') : 'N/A'}
                                        </Typography>
                                    </InfoCard>
                                    <InfoCard index={1} icon={<DescriptionIcon />} title="Details & Remarks">
                                        <Typography variant="subtitle2" fontWeight="bold">Customer Description:</Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            size="small"
                                            minRows={3}
                                            variant="outlined"
                                            value={selectedFeedback.description || ""}
                                            onChange={(e) => setSelectedFeedback(prev => ({ ...prev, description: e.target.value }))}
                                        />
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" mt={2} mb={2}>Admin Remarks:</Typography>
                                        <TextField // Changed from Typography to TextField for editability
                                            label="Add/Update Remarks"
                                            fullWidth
                                            multiline
                                            size="small"
                                            minRows={4}
                                            variant="outlined"
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            disabled={disableRemarksField} // This disables based on existing logic
                                        />
                                    </InfoCard>
                                    {selectedFeedback.images && selectedFeedback.images.trim() && (
                                        <InfoCard index={2} icon={<ImageIcon />} title="Attached Images">
                                            <Box display="flex" flexWrap="wrap" gap={1.5}>
                                                {selectedFeedback.images.split(",").map((img, idx) => (
                                                    <Box key={idx} component="img"
                                                        src={`${ServerBaseUrlForStaticImages}feedback/uploads/${img.trim()}`}
                                                        alt={`feedback-${idx}`}
                                                        onClick={() => handleImageClick(`${ServerBaseUrlForStaticImages}feedback/uploads/${img.trim()}`)}
                                                        sx={{
                                                            width: 90, height: 90, objectFit: "cover",
                                                            borderRadius: 2, border: '2px solid transparent', cursor: 'pointer',
                                                            transition: 'all 0.3s ease', '&:hover': { transform: 'scale(1.08)', borderColor: 'info.main', boxShadow: 5 }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </InfoCard>
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <InfoCard index={3} icon={<EditIcon />} title="Update Action">
                                    <Stack spacing={2}>
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel>Update Status</InputLabel>
                                            <Select
                                                label="Update Status"
                                                value={selectedFeedback?.status || ''}
                                                sx={{ height: 42 }}
                                                onChange={(e) => setSelectedFeedback(prev => ({ ...prev, status: e.target.value }))}
                                            >
                                                <MenuItem value="Pending">Pending</MenuItem>
                                                <MenuItem value="In Progress">In Progress</MenuItem>
                                                <MenuItem value="Resolved">Resolved</MenuItem>
                                            </Select>
                                            {selectedFeedback?.status === 'Resolved' && (
                                                <FormHelperText>Status is Resolved. You can change it if needed.</FormHelperText>
                                            )}
                                        </FormControl>
                                        <TextField
                                            label="Add/Update Remarks"
                                            fullWidth
                                            multiline
                                            size="small"
                                            minRows={4}
                                            variant="outlined"
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            disabled={disableRemarksField}
                                        />
                                    </Stack>
                                </InfoCard>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <Button onClick={handleCloseView} color="secondary">Close</Button>
                    <MDButton
                        onClick={handleStatusChange}
                        variant="contained"
                        color="info"
                        disabled={isSaveButtonDisabled}
                    >
                        {isUpdating ? "Updating..." : "Update & Save"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={openZoom} onClose={() => setOpenZoom(false)} maxWidth="lg" fullWidth>
                <Paper sx={{ position: 'relative', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
                    <IconButton onClick={() => setOpenZoom(false)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                    <img src={zoomImageUrl} alt="Zoomed View" style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }} />
                </Paper>
            </Dialog>
        </DashboardLayout>
    );
}

export default FeedbackViewPage;